/**
 * 数量・単価・販売価格などの数値入力欄 共通フォーマッタ
 *
 * 対象クラス: .quantity, .unit-price, .cost-price
 * （見積書・受注書・発注書・請求書の新規作成／編集画面で共通利用）
 * 金額入力欄の .js-amount-input は app.js で全画面共通に初期化する
 * （請求書繰越の入金額・その他調整・初回繰越設定など）。
 *
 * 前提: 対象inputは type="text" であること（type="number"はカンマ・全角文字を
 * 受け付けないため不可）。
 *
 * inputmode="decimal"について: Windows上のChrome/Edgeでは、inputmode="decimal"
 * （および numeric/tel/email/url）を付与したinputにフォーカスすると、日本語IMEが
 * 自動的にオフ（直接入力）へ切り替わる仕様がある。見積書・受注書・発注書・請求書の
 * 明細行（数量・単価・原単価）はIMEのオン/オフをユーザー操作に委ねるため、意図的に
 * inputmode="decimal"を付与していない（2026-08-06付で撤去）。全角数字の半角変換は
 * 本ファイルのfocusout処理で担保しているため、inputmode指定は必須ではない。
 * （資金繰り画面のセル入力欄など、他の呼び出し元でinputmode="decimal"を付与するかは
 * 呼び出し側の任意判断）
 *
 * 仕様:
 * - 入力中の値が半角数字のみ（カンマ・マイナス・小数点は許容）の場合、
 *   input時にリアルタイムでカンマ区切り表示に整形する。
 * - 全角文字が含まれている間は、入力中は一切変換しない
 *   （変換すると入力中の挙動がおかしくなるため）。
 * - フォーカスが外れた時（blur）に、全角数字が含まれていれば半角へ変換し、
 *   そのうえでカンマ区切り表示に整形する。
 * - フォーカスが当たった時（focus）は、編集しやすいようカンマを外し生の数値に戻す。
 *
 * イベント委譲(document.addEventListener)で登録するため、
 * 明細行が動的に追加されるフォーム（selectProduct/insertRowAfter等）でも
 * 個別に再バインドする必要はない。
 */

// 全角数字・全角記号 → 半角変換（price-warning.js等、入力中の判定側からも使うためexport）
export function toHalfWidthNumber(str) {
    return str
        .replace(/[０-９]/g, function (c) {
            return String.fromCharCode(c.charCodeAt(0) - 0xFEE0);
        })
        .replace(/[．。]/g, '.')
        .replace(/[，、]/g, ',')
        .replace(/[－ー―−]/g, '-');
}

// 全角数字（または全角記号）が含まれているか
function containsFullWidth(str) {
    return /[０-９．。，、－ー―−]/.test(str);
}

// カンマ除去（保存・再計算・半角判定用の生の数値文字列を得る）
function stripComma(str) {
    return String(str).replace(/,/g, '');
}

// 半角の数値表現（数字・マイナス・小数点・カンマのみ）かどうか
function isHalfWidthNumeric(str) {
    if (str === '') return true;
    return /^-?[0-9,]*\.?[0-9]*$/.test(str);
}

// カンマ区切り表示に整形（小数はそのまま維持）。
// 初期値をJSから代入する画面(請求書繰越の初回繰越設定モーダル等)でも使うためexportする
export function formatWithComma(str) {
    const cleaned = stripComma(str).trim();
    if (cleaned === '' || cleaned === '-' || isNaN(Number(cleaned))) {
        return cleaned;
    }

    const isNegative = cleaned.startsWith('-');
    const unsigned = isNegative ? cleaned.slice(1) : cleaned;
    const [intPart, decPart] = unsigned.split('.');
    const formattedInt = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    let result = formattedInt;
    if (decPart !== undefined) {
        result += '.' + decPart;
    }
    return (isNegative ? '-' : '') + result;
}

// カーソル位置より前にある「カンマを除いた数字の桁数」を数える
function countDigitsBeforeCursor(value, cursorPos) {
    return stripComma(value.slice(0, cursorPos)).length;
}

// 「カンマを除いた数字の桁数」から、整形後の文字列上でのカーソル位置を求める
function findCursorPosByDigitCount(formatted, digitCount) {
    if (digitCount <= 0) return 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] !== ',') {
            count++;
            if (count === digitCount) return i + 1;
        }
    }
    return formatted.length;
}

// 入力/貼り付けされた文字列が許可対象か（半角/全角数字・小数点・マイナス・カンマのみ）
// マイナスは半角(-)・全角(－)・長音(ー)・ダッシュ(―)・数学記号のマイナス(−、U+2212)を許可する。
// 半角カンマ(,)は「1,000」等のコピー&ペーストで貼り付け文字列全体に含まれるため必須。
// これが抜けていると、beforeinputが貼り付け全体を1回のe.dataとして受け取った際に
// preventDefault()され、ペースト自体が丸ごと無効化されてしまう(2026-08-08発見)。
function isAllowedInputChars(str) {
    return /^[0-9０-９．。，、\.\-,－ー―−]*$/.test(str);
}

// 対象inputの初期値(サーバー側の生の値)をカンマ区切り表示に整形する。
// 全角が含まれていれば半角へ変換したうえで整形する。
function formatInitialValue(el) {
    let value = el.value;
    if (containsFullWidth(value)) {
        value = toHalfWidthNumber(value);
    }
    if (isHalfWidthNumeric(value)) {
        el.value = formatWithComma(value);
    }
}

// 初期化済みのselector。documentへのリスナー・MutationObserverはselectorごとに
// 1回だけ登録する(同じselectorで2回呼ぶと入力・送信処理が二重に走るため)
const initializedSelectors = new Set();

export function initNumericInputFormatter(selector) {
    const TARGET_SELECTOR = selector || '.quantity, .unit-price, .cost-price';

    // 呼び出し時点で既にDOMに存在する対象inputの初期値を整形する。
    // 動的に追加される行はMutationObserverで補足されるが、資金繰り画面の
    // セル入力欄のようにBlade側でd-none状態のまま最初からDOMに存在する
    // input(編集モード切替時にクラス操作でd-noneを外すだけで新規追加されない)は
    // MutationObserverでは捕捉できないため、初期化時にも整形しておく必要がある。
    document.querySelectorAll(TARGET_SELECTOR).forEach(formatInitialValue);

    if (initializedSelectors.has(TARGET_SELECTOR)) return;
    initializedSelectors.add(TARGET_SELECTOR);

    // 数字・記号以外の入力（キー入力・IME確定・貼り付け）を丸ごとブロックする。
    // e.data が null になる操作（Backspace/Delete/カーソル移動等）は対象外。
    document.addEventListener('beforeinput', function (e) {
        if (!e.target.matches(TARGET_SELECTOR)) return;
        if (e.data == null) return;
        if (!isAllowedInputChars(e.data)) {
            e.preventDefault();
        }
    });

    document.addEventListener('input', function (e) {
        if (!e.target.matches(TARGET_SELECTOR)) return;

        // IME変換確定前（コンポジション中）は一切値を書き換えない。
        // 全角数字は「半角で打鍵→変換キーで全角に確定→Enterで確定」という
        // 入力経路を通ることが多く、変換確定前のコンポジション中バッファは
        // まだ半角数字のまま(containsFullWidthがfalseになる)。この状態を
        // 素通りしてel.value/selectionRangeを書き換えると、ブラウザ側の
        // IMEコンポジション状態とDOM側の値がずれ、以降の打鍵位置が狂う
        // （6文字目付近から誤入力される不具合の原因）。isComposingで
        // コンポジション中かどうかを直接判定し、その間は何もしない。
        if (e.isComposing) return;

        const el = e.target;
        const before = el.value;

        // 全角が含まれている間は入力中の変換をしない（挙動がおかしくなるため）
        if (containsFullWidth(before)) return;

        // 半角数値表現でなければ何もしない（記号の誤入力途中などはそのまま）
        if (!isHalfWidthNumeric(before)) return;

        const cursorPos = el.selectionStart ?? before.length;
        const digitCount = countDigitsBeforeCursor(before, cursorPos);

        const formatted = formatWithComma(before);
        if (formatted !== before) {
            el.value = formatted;
            const newPos = findCursorPosByDigitCount(formatted, digitCount);
            el.setSelectionRange(newPos, newPos);
        }
    });

    document.addEventListener('focusin', function (e) {
        if (!e.target.matches(TARGET_SELECTOR)) return;
        e.target.value = stripComma(e.target.value);
    });

    document.addEventListener('focusout', function (e) {
        if (!e.target.matches(TARGET_SELECTOR)) return;

        const el = e.target;
        let value = el.value;

        // 全角が残っていればここで半角へ変換
        const hadFullWidth = containsFullWidth(value);
        if (hadFullWidth) {
            value = toHalfWidthNumber(value);
        }

        el.value = formatWithComma(value);

        // 全角→半角変換が行われた場合、カスタムイベントをdispatchして
        // 各画面のリスナーに再計算を依頼する
        if (hadFullWidth) {
            el.dispatchEvent(new CustomEvent('numericValueNormalized', { bubbles: true }));
        }

        // 既存の金額再計算処理がある場合は呼び出す（後方互換のため残す）
        if (typeof window.calculateAllDebounced === 'function') {
            window.calculateAllDebounced();
        } else if (typeof window.calculateAll === 'function') {
            window.calculateAll();
        }
    });

    // 送信直前にカンマを除去（サーバー側は既にstr_replaceで対応済みだが、二重の安全策として）
    document.addEventListener('submit', function (e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        form.querySelectorAll(TARGET_SELECTOR).forEach(function (el) {
            el.value = stripComma(el.value);
        });
    }, true);

    // 明細行が動的に追加された際（初期データ読込・複製・行追加等）、
    // サーバー側の生の値がそのままvalueに設定されカンマ未整形のまま
    // 表示されてしまう問題への対策。DOMへの行追加を監視し、
    // 追加された要素内の対象inputを初期値の時点で自動整形する。
    const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (node) {
                if (!(node instanceof HTMLElement)) return;
                const targets = node.matches(TARGET_SELECTOR)
                    ? [node]
                    : Array.from(node.querySelectorAll(TARGET_SELECTOR));
                targets.forEach(formatInitialValue);
            });
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
}
